import { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { authApi } from '../api/auth';
import { ApiException } from '../api/client';
import { Alert } from '../components/Alert';
import { Button } from '../components/Button';
import { AccentCard } from '../components/Card';
import { AuthLayout } from '../components/Layout';
import type { VerifyEmailResponse } from '../types/api';

type VerifyState =
  | { status: 'verifying' }
  | { status: 'success'; data: VerifyEmailResponse }
  | { status: 'error'; message: string };

export function VerifyPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';
  const [state, setState] = useState<VerifyState>({ status: 'verifying' });

  useEffect(() => {
    if (!token) {
      setState({ status: 'error', message: 'Link verifikasi tidak valid (token tidak ditemukan di URL).' });
      return;
    }

    const run = async () => {
      try {
        const data = await authApi.verifyEmail(token);
        setState({ status: 'success', data });
      } catch (err) {
        const message = err instanceof ApiException
          ? err.message
          : 'Gagal memverifikasi email. Silakan coba lagi atau daftar ulang.';
        setState({ status: 'error', message });
      }
    };
    run();
  }, [token]);

  const goToCompleteProfile = () => {
    if (state.status === 'success') {
      // Bawa kodeKlien + email via state react-router
      navigate('/lengkapi-profil', {
        state: { kodeKlien: state.data.kodeKlien, email: state.data.email },
        replace: true,
      });
    }
  };

  return (
    <AuthLayout>
      <AccentCard title="Verifikasi Email" subtitle="Langkah 2 dari 3">
        {state.status === 'verifying' && (
          <div className="py-6 text-center text-sm text-gray-600">
            Memverifikasi token...
          </div>
        )}

        {state.status === 'success' && (
          <>
            <Alert variant="success" title="Email terverifikasi">
              Email <strong>{state.data.email}</strong> berhasil diverifikasi.
              <div className="mt-1 text-xs">
                Kode Klien Anda: <code className="bg-green-100 px-1 rounded">{state.data.kodeKlien}</code>
              </div>
            </Alert>
            <div className="mt-5">
              <Button variant="primary" fullWidth onClick={goToCompleteProfile}>
                Lanjutkan Isi Profil
              </Button>
            </div>
          </>
        )}

        {state.status === 'error' && (
          <>
            <Alert variant="error">{state.message}</Alert>
            <div className="mt-5 space-y-2">
              <Link to="/register" className="block">
                <Button variant="primary" fullWidth>
                  Daftar Ulang
                </Button>
              </Link>
              <Link to="/login" className="block">
                <Button variant="outline" fullWidth>
                  Login (Jika Sudah Punya Akun)
                </Button>
              </Link>
            </div>
          </>
        )}
      </AccentCard>
    </AuthLayout>
  );
}
